// SPDX-License-Identifier: GPL-3.0-or-later
/** Engine/DOM-free complete-vector export wire contract and resource policy. */
import type { MaterializationRequest } from "./multikey-materializer";
import type { MultikeyExportSnapshot } from "./multikey-export-snapshot";
import type { MultikeyRangeMetadata } from "./multikey-worker-protocol";

/** Exact complete-document processing policy, independent of display materialization. */
export const MULTIKEY_EXPORT_PROFILE = "ulsbs-complete-vector-v1/pdf-lib-1.17.1";
/** Version of the export transport envelope. */
export const MULTIKEY_EXPORT_PROTOCOL_VERSION = 1;
/** Maximum canonical input and serialized result size (not a heap guarantee). */
export const MULTIKEY_MAX_EXPORT_BYTES = 256 * 1024 * 1024;
/** Maximum derived materialized batch, enforced by the export engine before import. */
export const MULTIKEY_MAX_EXPORT_BATCH_BYTES = 32 * 1024 * 1024;
/** Maximum complete-document object graph admitted by the engine. */
export const MULTIKEY_MAX_EXPORT_OBJECTS = 200_000;

/** One initialized physical worker accepts a single begin, bounded appends, then finish. */
export type MultikeyExportWorkerRequest = { readonly id: number; readonly generation: number } & (
  | { readonly type: "init" }
  | {
      readonly type: "begin";
      readonly data: ArrayBuffer;
      readonly snapshot: MultikeyExportSnapshot;
    }
  | {
      readonly type: "append";
      readonly data: ArrayBuffer;
      readonly range: MultikeyRangeMetadata;
      readonly request: MaterializationRequest;
    }
  | { readonly type: "finish" }
);

/** Correlated replies; only finish transfers a complete output buffer. */
export type MultikeyExportWorkerReply = { readonly id: number; readonly generation: number } & (
  | { readonly ok: false; readonly error: string }
  | {
      readonly ok: true;
      readonly type: "init";
      readonly protocolVersion: number;
      readonly profile: string;
      readonly materializerProfile: string;
    }
  | { readonly ok: true; readonly type: "begin" | "append" }
  | { readonly ok: true; readonly type: "finish"; readonly result: Uint8Array }
);
